import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { SurveyTemplate, SurveyQuestion } from '@/types/database';
import { Pencil, Trash2, Copy, Globe, Send, CheckCircle, XCircle, AlertCircle, MoreVertical, Eye, Users, BarChart2, MessageSquare, Calendar, BarChart as ChartBar, UserCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SurveyPreviewModal } from './SurveyPreviewModal';

interface SurveyTemplateWithQuestions extends SurveyTemplate {
  questions: SurveyQuestion[];
  metrics?: {
    total_invites: number;
    pending_invites: number;
    total_responses: number;
    completion_rate: number;
    total_views: number;
  };
}

interface SurveyListProps {
  templates: SurveyTemplateWithQuestions[];
  onEdit: (template: SurveyTemplateWithQuestions) => void;
  onDuplicate: (template: SurveyTemplateWithQuestions) => void;
  onPublish: (template: SurveyTemplate) => void;
  onUnpublish: (template: SurveyTemplate) => void;
  onDelete: (id: string) => void;
  onSend?: (template: SurveyTemplate) => void;
}

export function SurveyList({
  templates,
  onEdit,
  onDuplicate,
  onPublish,
  onUnpublish,
  onDelete,
  onSend
}: SurveyListProps) {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<SurveyTemplateWithQuestions | null>(null);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const descriptionRefs = useRef<{ [key: string]: HTMLParagraphElement | null }>({});

  useEffect(() => {
    setActiveMenu(null);
    setPreviewTemplate(null);
    setShowTooltip(null);
  }, [templates]);

  const toggleMenu = (id: string) => {
    setActiveMenu(activeMenu === id ? null : id);
  };

  const checkTruncated = (id: string) => {
    const element = descriptionRefs.current[id];
    if (element) {
      return element.scrollWidth > element.clientWidth;
    }
    return false;
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this survey? This action cannot be undone.')) {
      onDelete(id);
    }
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="min-w-full">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Survey
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {template.title}
                      </div>
                      {template.description && (
                        <div className="relative">
                          <p
                            ref={el => descriptionRefs.current[template.id] = el}
                            className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2"
                            onMouseEnter={() => {
                              if (checkTruncated(template.id)) {
                                setShowTooltip(template.id);
                              }
                            }}
                            onMouseLeave={() => setShowTooltip(null)}
                          >
                            {template.description}
                          </p>
                          {showTooltip === template.id && checkTruncated(template.id) && (
                            <div className="absolute left-0 top-0 mt-6 w-64 p-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50">
                              {template.description}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex items-center space-x-4 mt-2">
                        <span className={cn(
                          "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                          template.status === 'published'
                            ? "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400"
                            : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400"
                        )}>
                          {template.status === 'published' ? (
                            <>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Published
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Draft
                            </>
                          )}
                        </span>
                        <div className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          {template.questions?.length || 0} questions
                        </div>
                        {template.metrics && (
                          <>
                            <div className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                              <UserCheck className="h-3 w-3 mr-1" />
                              {template.metrics.total_responses} responses
                            </div>
                            <div className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                              <ChartBar className="h-3 w-3 mr-1" />
                              {template.metrics.completion_rate}% completion
                            </div>
                          </>
                        )}
                        <div className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                          <Calendar className="h-3 w-3 mr-1" />
                          Updated {new Date(template.updated_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="hidden sm:flex items-center justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPreviewTemplate(template)}
                        title="Preview"
                        className="flex-shrink-0"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(template)}
                        title="Edit"
                        className="flex-shrink-0"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDuplicate(template)}
                        title="Duplicate"
                        className="flex-shrink-0"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      {template.organization_id && (
                        <>
                          <Link
                            to={`/surveys/${template.id}/respondents`}
                            className={cn(
                              "inline-flex items-center justify-center rounded-md font-medium transition-colors",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                              "border border-gray-200 bg-white hover:bg-gray-100",
                              "dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700",
                              "h-8 px-3 text-sm"
                            )}
                            title="Manage Respondents"
                          >
                            <Users className="h-4 w-4" />
                          </Link>
                          <Link
                            to={`/reports/${template.id}`}
                            className={cn(
                              "inline-flex items-center justify-center rounded-md font-medium transition-colors",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                              "border border-gray-200 bg-white hover:bg-gray-100",
                              "dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700",
                              "h-8 px-3 text-sm"
                            )}
                            title="View Survey Reports"
                          >
                            <BarChart2 className="h-4 w-4" />
                          </Link>
                        </>
                      )}
                      {template.status === 'draft' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onPublish(template)}
                          title="Publish"
                          className="flex-shrink-0"
                        >
                          <Globe className="h-4 w-4" />
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onUnpublish(template)}
                            title="Unpublish"
                            className="flex-shrink-0"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                          {onSend && template.organization_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onSend(template)}
                              title="Send Survey"
                              className="flex-shrink-0"
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                          )}
                        </>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(template.id)}
                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex-shrink-0"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="sm:hidden">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleMenu(template.id)}
                        className="flex-shrink-0"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                      {activeMenu === template.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 border border-gray-200 dark:border-gray-700 z-50">
                          <button
                            onClick={() => {
                              setPreviewTemplate(template);
                              toggleMenu(template.id);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Preview
                          </button>
                          <button
                            onClick={() => {
                              onEdit(template);
                              toggleMenu(template.id);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              onDuplicate(template);
                              toggleMenu(template.id);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </button>
                          {template.organization_id && (
                            <>
                              <Link
                                to={`/surveys/${template.id}/respondents`}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={() => toggleMenu(template.id)}
                              >
                                <Users className="h-4 w-4 mr-2" />
                                Manage Respondents
                              </Link>
                              <Link
                                to={`/reports/${template.id}`}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                onClick={() => toggleMenu(template.id)}
                              >
                                <BarChart2 className="h-4 w-4 mr-2" />
                                View Reports
                              </Link>
                            </>
                          )}
                          {template.status === 'draft' ? (
                            <button
                              onClick={() => {
                                onPublish(template);
                                toggleMenu(template.id);
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <Globe className="h-4 w-4 mr-2" />
                              Publish
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  onUnpublish(template);
                                  toggleMenu(template.id);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Unpublish
                              </button>
                              {onSend && template.organization_id && (
                                <button
                                  onClick={() => {
                                    onSend(template);
                                    toggleMenu(template.id);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  Send Survey
                                </button>
                              )}
                            </>
                          )}
                          <button
                            onClick={() => {
                              handleDelete(template.id);
                              toggleMenu(template.id);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SurveyPreviewModal
        isOpen={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        template={previewTemplate || { title: '', description: '', questions: [] }}
      />
    </>
  );
}